import React, { useEffect, useRef, useState, useCallback } from 'react'
import './ScrollablePanel.scss'

interface ScrollablePanelProps {
  children: React.ReactNode
  /** Pass the className to the inner scroll container for additional Ant targeting */
  className?: string
  style?: React.CSSProperties
}

/**
 * A wrapper that renders ghost scroll-indicator arrows (▲ / ▼) that
 * fade in/out based on scroll position.  The scrollbar itself is hidden.
 */
const ScrollablePanel: React.FC<ScrollablePanelProps> = ({
  children,
  className = '',
  style
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showTop, setShowTop] = useState(false)
  const [showBottom, setShowBottom] = useState(false)

  const update = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    setShowTop(scrollTop > 8)
    setShowBottom(scrollTop + clientHeight < scrollHeight - 8)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    update() // check on mount
    el.addEventListener('scroll', update, { passive: true })
    // Also recheck when content changes (e.g. menu expands)
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [update])

  return (
    <div className='scroll-panel' style={style}>
      {/* Top fade + chevron */}
      <div
        className={`scroll-indicator scroll-indicator--top ${showTop ? 'scroll-indicator--visible' : ''}`}
        aria-hidden='true'
      >
        <span className='scroll-chevron'>&#8963;</span>
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} className={`scroll-panel__inner ${className}`}>
        {children}
      </div>

      {/* Bottom fade + chevron */}
      <div
        className={`scroll-indicator scroll-indicator--bottom ${showBottom ? 'scroll-indicator--visible' : ''}`}
        aria-hidden='true'
      >
        <span className='scroll-chevron'>&#8964;</span>
      </div>
    </div>
  )
}

export default ScrollablePanel
